// src/hooks/useProfileCompletion.js
import { useMemo, useEffect, useRef, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { fetchMedicalInfoByUserId } from "../Profile/redux/thunks/medicalThunks";
import { fetchBmiHistoryByUserId } from "../Profile/redux/thunks/bmiHistoryThunks";
import {
  selectEffectiveUser,
  selectEffectiveUserId,
} from "../sous-profile/redux/selectors/sousProfileSelectors";
import Cookies from "js-cookie";
const isEmpty = (v) => {
  if (v === null || v === undefined) return true;

  if (typeof v === "string") {
    const s = v.trim().toLowerCase();
    if (s === "none" || s === "aucun" || s === "aucune") return false;
    return !s || s === "null" || s === "undefined" || s === "n/a";
  }

  if (Array.isArray(v)) return v.length === 0;

  if (typeof v === "object") {
    const keys = Object.keys(v || {});
    if (keys.length === 0) return true;
    return keys.every((k) => isEmpty(v[k]));
  }

  return false;
};

const getByPath = (obj, path) => {
  try {
    return path.split(".").reduce((acc, k) => acc?.[k], obj);
  } catch {
    return undefined;
  }
};

const pickFirstNonEmpty = (obj, paths) => {
  for (const p of paths) {
    const val = getByPath(obj, p);
    if (!isEmpty(val)) return val;
  }
  return undefined;
};

const normalizeUser = (u) => {
  if (!u) return null;
  return Array.isArray(u) ? u[0] : u;
};

const getStoredUserId = () => {
  try {
    const raw = localStorage.getItem("user") || Cookies.get("user");
    if (raw) {
      const parsed = JSON.parse(raw);
      return parsed?.id ?? parsed?.userId ?? parsed?._id ?? parsed?.pgId ?? null;
    }
  } catch (_) {}
  return (
    Cookies.get("userId") ||
    Cookies.get("id") ||
    Cookies.get("pgId") ||
    null
  );
};

const normalizeBlood = (v) => {
  if (!v) return "";
  return String(v).trim().toUpperCase().replace(/\s+/g, "");
};

const extractDiseasesFilled = (medicalData) => {
  const raw =
    medicalData?.maladiesChroniques ??
    medicalData?.chronicDiseases ??
    medicalData?.diseases ??
    null;

  if (raw == null) return false;

  if (Array.isArray(raw)) return raw.filter(Boolean).length > 0;

  const s = String(raw).trim();
  if (!s) return false;

  // JSON string
  if (s.startsWith("[") || s.startsWith("{")) {
    try {
      const parsed = JSON.parse(s);
      if (Array.isArray(parsed)) return parsed.filter(Boolean).length > 0;
      if (typeof parsed === "object") return Object.keys(parsed || {}).length > 0;
      return !isEmpty(parsed);
    } catch {
      // fallback: keep string
    }
  }

  return !isEmpty(s);
};

export default function useProfileCompletion(userIdOrOptions, maybeOptions = {}) {
  const explicitUserId =
    typeof userIdOrOptions === "string" || typeof userIdOrOptions === "number"
      ? String(userIdOrOptions)
      : null;
  const rawOptions = explicitUserId ? maybeOptions : userIdOrOptions;
  const safeOptions = rawOptions && typeof rawOptions === "object" ? rawOptions : {};
  const { enabled = true } = safeOptions;
  const dispatch = useDispatch();
  const patientUser = useSelector((s) => s.user?.data || null);
  const effectiveUser = useSelector(selectEffectiveUser);
  const effectiveUserId = useSelector(selectEffectiveUserId);
  const currentUser = useMemo(
    () => normalizeUser(patientUser) || normalizeUser(effectiveUser) || null,
    [patientUser, effectiveUser]
  );
  const userId = useMemo(() => {
    const u = currentUser || {};
    return (
      explicitUserId ||
      effectiveUserId ||
      u?.id ||
      u?.userId ||
      u?._id ||
      u?.pgId ||
      u?.raw?.id ||
      u?.raw?.userId ||
      u?.raw?._id ||
      getStoredUserId()
    );
  }, [currentUser, effectiveUserId, explicitUserId]);
  const medicalState = useSelector((s) => s.medical);
  const medicalData = userId
    ? medicalState?.byId?.[userId] ?? medicalState?.data ?? null
    : null;
  const medicalLoading = !!medicalState?.loading;
  const bmiHistoryState = useSelector((s) => s.bmiHistory);
  const bmiArr = bmiHistoryState?.data ?? bmiHistoryState?.items ?? [];
  const bmiLoading = !!bmiHistoryState?.loading;
  const [tick, setTick] = useState(0);
  const lastFetchedRef = useRef("");
  useEffect(() => {
    const onUpd = () => setTick((t) => t + 1);
    window.addEventListener("profileUpdated", onUpd);
    window.addEventListener("doctorProfileUpdated", onUpd);
    return () => {
      window.removeEventListener("profileUpdated", onUpd);
      window.removeEventListener("doctorProfileUpdated", onUpd);
    };
  }, []);
  useEffect(() => {
    if (!enabled) return;
    if (!userId) return;
    const key = `${userId}:${tick}`;
    if (lastFetchedRef.current === key) return;
    lastFetchedRef.current = key;
    dispatch(fetchMedicalInfoByUserId(userId));
    dispatch(fetchBmiHistoryByUserId(userId));
  }, [dispatch, userId, enabled, tick]);
  const computed = useMemo(() => {
    const u = currentUser || {};
    const email = pickFirstNonEmpty(u, [
      "email",
      "user.email",
      "account.email",
      "contact.email",
      "profile.email",
      "raw.email",
    ]);
    const phone = pickFirstNonEmpty(u, [
      "phone",
      "phoneNumber",
      "phone_number",
      "mobile",
      "tel",
      "telephone",
      "contact.phone",
      "user.phone",
      "profile.phone",
      "profile.phoneNumber",
      "raw.phone",
      "raw.phoneNumber",
    ]);
    const avatar = pickFirstNonEmpty(u, [
      "avatar",
      "avatarUrl",
      "photo",
      "image",
      "profile.avatar",
      "profile.avatarUrl",
      "profile.photo",
      "raw.avatar",
      "raw.avatarUrl",
      "raw.photo",
    ]);
    const latestBmi =
      Array.isArray(bmiArr) && bmiArr.length ? bmiArr[bmiArr.length - 1] : null;
    const height =
      latestBmi?.tailleCm ??
      latestBmi?.height ??
      medicalData?.tailleCm ??
      medicalData?.height ??
      null;
    const weight =
      latestBmi?.poidsKg ??
      latestBmi?.weight ??
      medicalData?.poidsKg ??
      medicalData?.weight ??
      null;
    const blood =
      normalizeBlood(
        medicalData?.groupeSanguin ??
          medicalData?.bloodType ??
          medicalData?.blood ??
          null
      ) || "";
    const diseasesFilled = extractDiseasesFilled(medicalData);
    const REQUIRED = [
      { key: "email", label: "Email", ok: !isEmpty(email) },
      { key: "phone", label: "Phone", ok: !isEmpty(phone) },
      { key: "avatar", label: "Profile picture", ok: !isEmpty(avatar) },
      { key: "blood", label: "Blood type", ok: !isEmpty(blood) },
      { key: "height", label: "Height", ok: height != null && String(height).trim() !== "" },
      { key: "weight", label: "Weight", ok: weight != null && String(weight).trim() !== "" },
      { key: "diseases", label: "Diseases", ok: diseasesFilled },
    ];
    const missing = REQUIRED.filter((f) => !f.ok).map((f) => f.label);
    const total = REQUIRED.length;
    const done = total - missing.length;
    const percent = total === 0 ? 0 : Math.round((done / total) * 100);

    return {
      percent,
      missing,
      done,
      total,
      debug: {
        userId,
        email,
        phone,
        avatar,
        height,
        weight,
        blood,
        diseasesFilled,
      },
    };
  }, [currentUser, medicalData, bmiArr, userId]);

  return {
    loading: enabled ? medicalLoading || bmiLoading : false,
    percent: computed.percent,
    missing: computed.missing,
    done: computed.done,
    total: computed.total,
    debug: computed.debug,
  };
}
